import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import apiCaller from 'services/Api/ApiCaller';
import { SESSION_PRESENCE_CHANGED } from 'services/sessionPresence';
import { API_URL } from 'settings';

type Players = Record<number, { users: number; expires_at: number }>;
const Presence = createContext<Players | undefined>(undefined);

export function SessionPresenceProvider({
  sessionId,
  children,
}: {
  sessionId: number;
  children: React.ReactNode;
}) {
  const [players, setPlayers] = useState<Players>();
  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    let expiry: ReturnType<typeof setTimeout>;
    let nextRead = 0;
    let lastRead = 0;
    let changed = false;
    let loading = false;
    // Socket hints only invalidate this snapshot. Combine busy joins/logouts and
    // retain a hint received while a read is running; never poll every player.
    const due = () =>
      changed ? Math.min(nextRead, lastRead + 2000) : nextRead;
    setPlayers(undefined);
    const load = async () => {
      if (controller.signal.aborted || loading) return;
      clearTimeout(timer);
      if (document.visibilityState !== 'visible') return;
      if (Date.now() < due()) {
        timer = setTimeout(load, due() - Date.now());
        return;
      }
      loading = true;
      changed = false;
      lastRead = Date.now();
      nextRead = lastRead + 60000;
      try {
        const response = await apiCaller({
          apiUrl: API_URL,
          path: `/presence/sessions/${sessionId}/`,
          signal: controller.signal,
        });
        if (controller.signal.aborted || response.status !== 200) return;
        const now = Date.now() / 1000;
        const next: Players = Object.fromEntries(
          Object.entries(response.result.players).map(([id, value]) => [
            id,
            {
              ...(value as Players[number]),
              expires_at:
                now +
                (value as Players[number]).expires_at -
                response.result.as_of,
            },
          ]),
        );
        setPlayers(next);
        clearTimeout(expiry);
        const expire = () => {
          if (controller.signal.aborted) return;
          const live = Object.fromEntries(
            Object.entries(next).filter(
              ([, p]) => p.expires_at > Date.now() / 1000,
            ),
          );
          setPlayers(live);
          const due = Math.min(...Object.values(live).map(p => p.expires_at));
          if (Number.isFinite(due))
            expiry = setTimeout(expire, Math.max(100, due * 1000 - Date.now()));
        };
        expire();
      } catch {
        // Keep known deadlines; a read failure does not alter session data.
      } finally {
        loading = false;
        if (!controller.signal.aborted)
          timer = setTimeout(load, Math.max(0, due() - Date.now()));
      }
    };
    const resume = () => {
      void load();
    };
    const invalidate = (event: Event) => {
      if ((event as CustomEvent<number>).detail !== sessionId) return;
      changed = true;
      void load();
    };
    window.addEventListener(SESSION_PRESENCE_CHANGED, invalidate);
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('focus', resume);
    void load();
    return () => {
      controller.abort();
      clearTimeout(timer);
      clearTimeout(expiry);
      window.removeEventListener(SESSION_PRESENCE_CHANGED, invalidate);
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('focus', resume);
    };
  }, [sessionId]);
  return <Presence.Provider value={players}>{children}</Presence.Provider>;
}

export function usePresencePlayers<
  T extends { id: number; online: boolean | null },
>(players: T[]): T[] {
  const presence = useContext(Presence);
  return useMemo(
    () =>
      presence === undefined
        ? players
        : players.map(player => ({
            ...player,
            online: !!presence[player.id]?.users,
          })),
    [players, presence],
  );
}
